import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, MapPin, Calendar, Users, ChevronDown, Instagram, Star, Quote, ExternalLink } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Use local transparent logo (white background removed via Pillow)
const LOGO_URL = "/images/logo-transparent.png";

// Real customer reviews from Google/Tripadvisor
const customerReviews = [
  {
    name: "Happy Customer",
    rating: 5,
    text: "The best Nasi Lemak in the whole of East London. The sambal is unbeatable!",
    source: "Google"
  },
  {
    name: "Coffee Lover",
    rating: 5,
    text: "Truly a gem of a cafe. Exceptional pastries, delicious hot drinks, and beautiful decor.",
    source: "Google"
  },
  {
    name: "Local Regular",
    rating: 5,
    text: "Run by close friends who have poured their heart and soul into creating a warm and welcoming space. More than just a place to grab a coffee.",
    source: "Google"
  }
];

// Featured menu items with images
// Featured menu items with user's uploaded images (cropped to food focus)
const featuredMenuItems = [
  {
    name: 'Nasi Lemak',
    description: 'The quintessential Malay breakfast with our secret Sambal',
    price: '£11.95',
    badge: 'SIGNATURE',
    image: 'https://customer-assets.emergentagent.com/job_3d290920-bb19-4016-858f-1f272e69a4f5/artifacts/0cphl1so_nasi%20lemak.png',
    cropPosition: 'center 40%'
  },
  {
    name: 'Laksa',
    description: 'Creamy coconut curry broth - MSG free!',
    price: '£15.50',
    badge: 'AUTHENTIC',
    image: 'https://customer-assets.emergentagent.com/job_3d290920-bb19-4016-858f-1f272e69a4f5/artifacts/4c7c8ply_Laksa.png',
    cropPosition: 'center 35%'
  },
  {
    name: 'Murtabak',
    description: 'Malaysian stuffed pan fried folded bread delicacy',
    price: '£8.50',
    badge: 'MALAYSIAN',
    image: 'https://customer-assets.emergentagent.com/job_3d290920-bb19-4016-858f-1f272e69a4f5/artifacts/se2yyd3q_Murtabak%20image.jpg',
    cropPosition: 'center center'
  }
];

const stats = [
  { number: '5.0', label: 'Google Rating' },
  { number: '#1', label: 'Coffee Spot in Woodford' },
  { number: '100%', label: 'Organic Houseblend' },
  { number: '200+', label: '5-Star Reviews' },
];

export default function HomePage() {
  const [reservationData, setReservationData] = useState({
    name: '',
    email: '',
    date: '',
    time: '',
    guests: '2'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reservationSuccess, setReservationSuccess] = useState(false);

  const handleReservationSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/reservations`, reservationData);
      setReservationSuccess(true);
      setReservationData({ name: '', email: '', date: '', time: '', guests: '2' });
    } catch (error) {
      console.error('Reservation error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section with Cream Background and Artisan Pattern */}
      <section className="relative min-h-screen flex items-center" style={{ backgroundColor: '#F5F0E6' }} data-testid="hero-section">
        {/* Artisan branding background - hand-drawn coffee, pastries, herbs illustration */}
        <div 
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: `url("/images/artisan-background.png")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        
        <div className="relative container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="max-w-3xl mx-auto text-center pt-24">
            {/* Actual Logo with background removal */}
            <div
              className="mb-10"
            >
              <img
                src={LOGO_URL}
                alt="Honey I'm Home"
                className="h-24 md:h-36 w-auto mx-auto"
              />
            </div>
            
            <p
              className="font-serif text-xl md:text-2xl text-neutral-700 italic mb-6"
            >
              Organic artisan coffee experience and Makan
            </p>

            <div
              className="space-y-4 mb-10"
            >
              <p className="text-neutral-600 text-lg max-w-xl mx-auto">
                <span className="font-semibold">Organic Houseblend Coffee</span> · <span className="font-semibold">Malaysian Fusion Brunch</span> · <span className="font-semibold">Freshly Baked Pastries</span>
              </p>
              <p className="text-neutral-500 max-w-lg mx-auto">
                Award-winning café in Woodford Green, East London. Authentic Malaysian dishes 
                made with our secret family recipes.
              </p>
              <p className="text-neutral-500 text-sm italic">(passed down to my mum from our Nannyma)</p>
            </div>
            
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/menu"
                data-testid="hero-explore-menu-btn"
                className="inline-flex items-center justify-center bg-black text-white px-8 py-4 text-xs tracking-widest uppercase font-bold hover:bg-neutral-800 transition-all"
              >
                Explore Menu
                <ArrowRight size={16} className="ml-2" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-reserve-btn"
                className="inline-flex items-center justify-center border-2 border-black text-black px-8 py-4 text-xs tracking-widest uppercase font-bold hover:bg-black hover:text-white transition-all"
              >
                Reserve a Table
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <ChevronDown size={24} className="animate-bounce text-neutral-400" />
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 border-b border-neutral-200 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-testid="stats-section">
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                className="text-center"
              >
                <p className="font-serif text-4xl md:text-5xl font-bold text-black mb-2">{stat.number}</p>
                <p className="text-xs tracking-[0.2em] text-neutral-500 uppercase">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We're Known For - with cream background and artisan pattern */}
      <section className="relative py-24 md:py-32" style={{ backgroundColor: '#F5F0E6' }} data-testid="highlights-section">
        {/* Artisan background pattern */}
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: `url("/images/artisan-background.png")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        <div className="relative container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] text-neutral-500 uppercase mb-4">What We're Known For</p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-black">
              Three Things We <span className="italic">Love</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Organic Coffee */}
            <div
              className="text-center p-8 bg-white shadow-sm"
            >
              <div className="text-5xl mb-6">☕</div>
              <h3 className="font-serif text-2xl font-semibold text-black mb-4">Organic Houseblend</h3>
              <p className="text-neutral-600 leading-relaxed">
                Our signature organic houseblend coffee, carefully sourced and roasted to perfection. 
                Every cup is crafted with love by our skilled baristas.
              </p>
            </div>

            {/* Malaysian Fusion */}
            <div
              className="text-center p-8 bg-white shadow-sm"
            >
              <div className="text-5xl mb-6">🍜</div>
              <h3 className="font-serif text-2xl font-semibold text-black mb-4">Malaysian Fusion Brunch</h3>
              <p className="text-neutral-600 leading-relaxed">
                Unique Malaysian dishes made with our secret family recipes. Our Nasi Lemak 
                and Laksa have earned us the title of "Best in East London."
              </p>
            </div>

            {/* Fresh Pastries */}
            <div
              className="text-center p-8 bg-white shadow-sm"
            >
              <div className="text-5xl mb-6">🥐</div>
              <h3 className="font-serif text-2xl font-semibold text-black mb-4">Freshly Baked Pastries</h3>
              <p className="text-neutral-600 leading-relaxed">
                Delicious pastries baked fresh daily. From buttery croissants to indulgent 
                cinnamon rolls, every bite is a moment of pure joy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* GoodCup Sustainability Section */}
      <section className="py-20 md:py-28 bg-[#1a472a] text-white overflow-hidden" data-testid="goodcup-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div
              className="order-2 lg:order-1"
            >
              <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-6">
                <span className="text-xl">🌱</span>
                <span className="text-xs tracking-wider uppercase font-medium">Sustainability Partner</span>
              </div>
              
              <h2 className="font-serif text-4xl md:text-5xl font-bold mb-6">
                Better Coffee, <span className="italic">Better Planet</span>
              </h2>
              
              <p className="text-white/80 text-lg leading-relaxed mb-6">
                We're proud to partner with <strong className="text-white">GoodCup</strong> for our takeaway cups. 
                Every cup is 100% recyclable and made from sustainably sourced materials.
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-white/10 p-4 rounded-lg">
                  <div className="text-3xl mb-2">♻️</div>
                  <p className="font-semibold text-sm">100% Recyclable</p>
                  <p className="text-white/60 text-xs">Fully recyclable cups</p>
                </div>
                <div className="bg-white/10 p-4 rounded-lg">
                  <div className="text-3xl mb-2">🌿</div>
                  <p className="font-semibold text-sm">Plant-Based</p>
                  <p className="text-white/60 text-xs">Sustainable materials</p>
                </div>
                <div className="bg-white/10 p-4 rounded-lg">
                  <div className="text-3xl mb-2">🌍</div>
                  <p className="font-semibold text-sm">Carbon Neutral</p>
                  <p className="text-white/60 text-xs">Reduced footprint</p>
                </div>
                <div className="bg-white/10 p-4 rounded-lg">
                  <div className="text-3xl mb-2">☕</div>
                  <p className="font-semibold text-sm">Great Taste</p>
                  <p className="text-white/60 text-xs">No compromise</p>
                </div>
              </div>
              
              <a 
                href="https://www.good-cup.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm"
              >
                Learn more about GoodCup
                <ExternalLink size={14} />
              </a>
            </div>
            
            {/* Right - Actual Cup Image */}
            <div
              className="order-1 lg:order-2 flex justify-center"
            >
              <div className="relative">
                {/* Decorative circles */}
                <div className="absolute -top-8 -right-8 w-64 h-64 bg-white/5 rounded-full" />
                <div className="absolute -bottom-4 -left-4 w-48 h-48 bg-white/5 rounded-full" />
                
                {/* Actual cup image */}
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_79e6a303-daea-4542-8568-b363cbb6a50e/artifacts/5mxm62i3_Screenshot_20260223_025737.jpg"
                    alt="Honey I'm Home eco-friendly GoodCup takeaway cups"
                    className="w-full max-w-md h-auto object-cover"
                    style={{ maxHeight: '500px' }}
                  />
                  
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a472a]/60 to-transparent" />
                  
                  {/* Badge overlay */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3 flex items-center justify-between">
                      <div>
                        <p className="text-[#1a472a] font-bold text-sm">GOODCUP Partner</p>
                        <p className="text-[#1a472a]/70 text-xs">Reduce. Reuse. Recycle.</p>
                      </div>
                      <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                        ECO ✓
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating badges */}
                <div 
                  className="absolute -top-2 -right-2 bg-green-400 text-green-900 px-3 py-1 rounded-full text-xs font-bold shadow-lg"
                >
                  100% RECYCLABLE
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="py-24 md:py-32 bg-black text-white" data-testid="reviews-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <div className="flex justify-center items-center gap-1 mb-4">
              {[1,2,3,4,5].map(i => (
                <Star key={i} size={24} className="text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <p className="text-xs tracking-[0.2em] text-neutral-400 uppercase mb-4">What Our Guests Say</p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold">
              5.0 Rating on <span className="italic">Google</span>
            </h2>
            <p className="text-neutral-400 mt-4">#1 Coffee & Tea Spot in Woodford Green · Tripadvisor Travellers' Choice</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {customerReviews.map((review, index) => (
              <div
                key={index}
                className="bg-white/5 p-8"
                data-testid={`review-${index}`}
              >
                <Quote size={32} className="text-neutral-600 mb-4" />
                <p className="text-neutral-200 leading-relaxed mb-6 italic">
                  "{review.text}"
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} size={14} className="text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <span className="text-neutral-500 text-sm">{review.source}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Menu with Images */}
      <section className="py-24 md:py-32" style={{ backgroundColor: '#F5F0E6' }} data-testid="featured-menu">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] text-neutral-500 uppercase mb-4">Malaysian Fusion Brunch</p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-black">
              Family <span className="italic">Recipes</span>
            </h2>
            <p className="text-neutral-500 text-sm italic mt-2">(passed down to my mum from our Nannyma)</p>
            <p className="text-neutral-500 mt-4 max-w-xl mx-auto">
              Authentic Malaysian dishes made with secret family recipes passed down through generations
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredMenuItems.map((item, index) => (
              <div
                key={item.name}
                className="group bg-white overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500"
                data-testid={`featured-item-${index}`}
              >
                {/* Food Image - cropped to focus on food */}
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    style={{ objectPosition: item.cropPosition || 'center center' }}
                  />
                  {item.badge && (
                    <span className="absolute top-4 left-4 text-[10px] tracking-wider uppercase bg-black text-white px-3 py-1">
                      {item.badge}
                    </span>
                  )}
                </div>
                {/* Content */}
                <div className="p-6 text-center">
                  <h3 className="font-serif text-2xl font-medium text-black mb-2">{item.name}</h3>
                  <p className="text-neutral-500 text-sm mb-4">{item.description}</p>
                  <p className="font-serif text-2xl font-semibold text-black">{item.price}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/menu"
              data-testid="view-full-menu-btn"
              className="inline-flex items-center justify-center bg-black text-white px-8 py-4 text-xs tracking-widest uppercase font-bold hover:bg-neutral-800 transition-all"
            >
              View Full Menu
              <ArrowRight size={16} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery Section - Our Food & Drinks */}
      <section className="py-24 md:py-32 bg-white" data-testid="gallery-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.2em] text-neutral-500 uppercase mb-4">Our Creations</p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-black mb-4">
              Gallery
            </h2>
            <p className="text-neutral-500 max-w-lg mx-auto">
              A taste of what awaits you at Honey I'm Home
            </p>
            
            {/* Instagram Profile Badge */}
            <a
              href="https://www.instagram.com/honeyimhome.coffee/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-follow-link"
              className="inline-block mt-6"
            >
              <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 p-0.5 rounded-full">
                <div className="bg-white rounded-full px-5 py-2 flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 rounded-full flex items-center justify-center">
                    <Instagram size={16} className="text-white" />
                  </div>
                  <span className="font-medium text-sm text-black">@honeyimhome.coffee</span>
                  <ArrowRight size={14} className="text-neutral-400" />
                </div>
              </div>
            </a>
          </div>
          
          {/* Gallery Grid - User uploaded images */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {[
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/5p6h0udy_Screenshot_20260223_041650_Instagram.jpg', alt: 'Laksa - Malaysian curry noodle soup' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/uk598u9x_Screenshot_20260223_041656_Instagram.jpg', alt: 'Nasi Lemak with French omelette' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/8zixv1ip_Screenshot_20260223_041731_Instagram.jpg', alt: 'Gelato with toppings' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/s1rrg7im_Screenshot_20260223_041920_Instagram.jpg', alt: 'Nasi Lemak - Malaysian coconut rice' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/100lf3aj_Screenshot_20260223_041955_Instagram.jpg', alt: 'Signature iced drink' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/fly9qe4n_Screenshot_20260223_041427_Instagram.jpg', alt: 'Grilled sandwich with flowers' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/8ae7apwz_Screenshot_20260223_041434_Instagram.jpg', alt: 'Apple turnover with ice cream' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/v8mcbqzw_Screenshot_20260223_041514_Instagram.jpg', alt: 'Grilled panini sandwich' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/h62s8bau_Screenshot_20260223_041544_Instagram.jpg', alt: 'Golden turmeric latte' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/5vt70320_Screenshot_20260223_041602_Instagram.jpg', alt: 'Fresh salad with fried chicken' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/z4n4v25l_Screenshot_20260223_041336_Instagram.jpg', alt: 'Iced coffee with branding' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/ikeegoxe_Screenshot_20260223_041359_Instagram.jpg', alt: 'Matcha lattes' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/iwup6wtu_Screenshot_20260223_041408_Instagram.jpg', alt: 'Golden latte with art' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/2dujnef2_Screenshot_20260223_041416_Instagram.jpg', alt: 'Latte with chocolate shavings' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/csr52hkc_Screenshot_20260223_041422_Instagram.jpg', alt: 'Chocolate raspberry tarts' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/7isfq9g8_Screenshot_20260223_041210_Instagram.jpg', alt: 'Iced matcha drinks lineup' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/qjmyijwu_Screenshot_20260223_041216_Instagram.jpg', alt: 'Cinnamon latte art' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/4fvb3f54_Screenshot_20260223_041245_Instagram.jpg', alt: 'Latte art rosetta' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/nh7ir0qc_Screenshot_20260223_041250_Instagram.jpg', alt: 'Coffee on silver tray' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/y44ual7m_Screenshot_20260223_041308_Instagram.jpg', alt: 'Affogato - gelato with espresso' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/fz1mjpre_Screenshot_20260223_041101_Instagram.jpg', alt: 'Espresso pour' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/3fiig4ir_Screenshot_20260223_041106_Instagram.jpg', alt: 'Murtabak with salad' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/5hkftk94_Screenshot_20260223_041111_Instagram.jpg', alt: 'Fresh croissants' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/ldpk43rk_Screenshot_20260223_041132_Instagram.jpg', alt: 'Pistachio swirl pastries' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/w3qid24g_Screenshot_20260223_041150_Instagram.jpg', alt: 'Almond croissants' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/2e8asfl4_Screenshot_20260223_041020_Instagram.jpg', alt: 'Cinnamon swirls' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/e5xpanv1_Screenshot_20260223_041025_Instagram.jpg', alt: 'The Crookie' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/0ycx2qz8_Screenshot_20260223_041031_Instagram.jpg', alt: 'Weekend pastries display' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/28m89izo_Screenshot_20260223_041036_Instagram.jpg', alt: 'Apricot croissants' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/kttyyjww_Screenshot_20260223_041052_Instagram.jpg', alt: 'Pain suisse chocolate' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/aensszos_Screenshot_20260223_040749_Instagram.jpg', alt: 'Eclairs trio' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/hpuctya1_Screenshot_20260223_040754_Instagram.jpg', alt: 'Hot chocolate marshmallows' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/s7igosza_Screenshot_20260223_040806_Instagram.jpg', alt: 'Pistachio hot chocolate' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/cmj5zyuo_Screenshot_20260223_040841_Instagram.jpg', alt: 'Sambal Cilbir' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/pjq99b7w_Screenshot_20260223_040936_Instagram.jpg', alt: 'Hello Avocado with poached eggs' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/7vn2etv0_Screenshot_20260223_040744_Instagram.jpg', alt: 'Nasi Lemak on banana leaf' },
              { src: 'https://customer-assets.emergentagent.com/job_613d9870-32bb-4e09-a93a-6fd29666cc9c/artifacts/0t0svnlk_Screenshot_20260223_025737.jpg', alt: 'Branded takeaway cup' },
            ].map((image, index) => (
              <div
                key={index}
                className="aspect-square relative overflow-hidden bg-neutral-100 group"
                data-testid={`gallery-item-${index}`}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
                {/* Subtle hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
              </div>
            ))}
          </div>
          
          {/* View More on Instagram */}
          <div className="text-center mt-10">
            <a
              href="https://www.instagram.com/honeyimhome.coffee/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="view-instagram-btn"
              className="inline-flex items-center justify-center bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 text-white px-8 py-4 text-xs tracking-widest uppercase font-bold hover:opacity-90 transition-opacity"
            >
              <Instagram size={16} className="mr-2" />
              Follow on Instagram
              <ExternalLink size={14} className="ml-2" />
            </a>
          </div>
        </div>
      </section>


      {/* Quick Reservation Section */}
      <section className="py-24 md:py-32 bg-black text-white" data-testid="quick-reservation">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-xs tracking-[0.2em] text-neutral-400 uppercase mb-4">Make a Reservation</p>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold mb-6">
                Reserve your <span className="italic">table</span>
              </h2>
              <p className="text-neutral-400 leading-relaxed mb-8">
                Whether it's a morning coffee ritual, a business meeting, or catching up with friends, 
                we'd love to welcome you. Reserve your spot and let us take care of the rest.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Calendar size={18} className="mr-2 text-neutral-400" />
                  <span>Open 7 Days</span>
                </div>
                <div className="flex items-center">
                  <Users size={18} className="mr-2 text-neutral-400" />
                  <span>Groups Welcome</span>
                </div>
              </div>
            </div>
            
            <div className="p-8 md:p-10" style={{ backgroundColor: 'rgba(245, 240, 230, 0.1)' }}>
              {reservationSuccess ? (
                <div className="text-center py-8" data-testid="reservation-success">
                  <div className="text-4xl mb-4">✓</div>
                  <h3 className="font-serif text-2xl mb-2">Thank You!</h3>
                  <p className="text-neutral-400">We'll confirm your reservation shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleReservationSubmit} className="space-y-6">
                  <div>
                    <input
                      type="text"
                      value={reservationData.name}
                      onChange={(e) => setReservationData({...reservationData, name: e.target.value})}
                      placeholder="Your Name"
                      data-testid="reservation-name-input"
                      className="w-full bg-transparent border-b border-neutral-700 px-0 py-3 text-white focus:border-white focus:outline-none placeholder:text-neutral-500"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      value={reservationData.email}
                      onChange={(e) => setReservationData({...reservationData, email: e.target.value})}
                      placeholder="Email Address"
                      data-testid="reservation-email-input"
                      className="w-full bg-transparent border-b border-neutral-700 px-0 py-3 text-white focus:border-white focus:outline-none placeholder:text-neutral-500"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="date"
                      value={reservationData.date}
                      onChange={(e) => setReservationData({...reservationData, date: e.target.value})}
                      data-testid="reservation-date-input"
                      className="w-full bg-transparent border-b border-neutral-700 px-0 py-3 text-white focus:border-white focus:outline-none"
                      required
                    />
                    <input
                      type="time"
                      value={reservationData.time}
                      onChange={(e) => setReservationData({...reservationData, time: e.target.value})}
                      data-testid="reservation-time-input"
                      className="w-full bg-transparent border-b border-neutral-700 px-0 py-3 text-white focus:border-white focus:outline-none"
                      required
                    />
                  </div>
                  <div>
                    <select
                      value={reservationData.guests}
                      onChange={(e) => setReservationData({...reservationData, guests: e.target.value})}
                      data-testid="reservation-guests-select"
                      className="w-full bg-transparent border-b border-neutral-700 px-0 py-3 text-white focus:border-white focus:outline-none"
                    >
                      {[1,2,3,4,5,6,7,8].map(n => (
                        <option key={n} value={n} className="text-black">{n} {n === 1 ? 'Guest' : 'Guests'}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="reservation-submit-btn"
                    className="w-full bg-white text-black px-8 py-4 text-xs tracking-widest uppercase font-bold hover:bg-neutral-200 transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? 'Submitting...' : 'Request Reservation'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Franchise CTA */}
      <section className="py-24 md:py-32" style={{ backgroundColor: '#F5F0E6' }} data-testid="franchise-cta">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <div
          >
            <p className="text-xs tracking-[0.2em] text-neutral-500 uppercase mb-4">Grow With Us</p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-black mb-6">
              Franchise <span className="italic">Opportunity</span>
            </h2>
            <p className="text-neutral-600 leading-relaxed mb-8 max-w-2xl mx-auto">
              Join our growing family. We're looking for passionate partners who share our vision 
              for exceptional coffee and warm hospitality. Let's build something beautiful together.
            </p>
            <Link
              to="/franchise"
              data-testid="franchise-enquire-btn"
              className="inline-flex items-center justify-center bg-black text-white px-8 py-4 text-xs tracking-widest uppercase font-bold hover:bg-neutral-800 transition-all"
            >
              Enquire Now
              <ArrowRight size={16} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
